// Reportes del POS. POS → Reportes.
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { BarChart3, Coins, FileText, Loader2, PieChart, RefreshCw, TrendingUp, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Caja, cajasActivas, fieldCls, money, posRpc, fechaLocal } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const hoy = () => fechaLocal();
const mesInicio = () => { const d = new Date(); return fechaLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };

// Filtro de rango + caja (reutilizable)
function Filtros({ f, set, conCaja = true }: { f: Filtro; set: (p: Partial<Filtro>) => void; conCaja?: boolean }) {
  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-activas'], queryFn: cajasActivas, enabled: conCaja });
  return (
    <Card><CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 items-end">
      <label className="block space-y-1"><span className="text-xs text-muted-foreground">Desde</span><input type="date" className={fieldCls} value={f.desde} onChange={(e) => set({ desde: e.target.value })} /></label>
      <label className="block space-y-1"><span className="text-xs text-muted-foreground">Hasta</span><input type="date" className={fieldCls} value={f.hasta} onChange={(e) => set({ hasta: e.target.value })} /></label>
      {conCaja && (
        <label className="block space-y-1 md:col-span-2"><span className="text-xs text-muted-foreground">Caja</span>
          <select className={fieldCls} value={f.caja} onChange={(e) => set({ caja: e.target.value })}>
            <option value="">— Todas —</option>{cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select></label>
      )}
    </CardContent></Card>
  );
}
interface Filtro { desde: string; hasta: string; caja: string }
const useFiltro = () => { const [f, setF] = useState<Filtro>({ desde: mesInicio(), hasta: hoy(), caja: '' }); return { f, set: (p: Partial<Filtro>) => setF((s) => ({ ...s, ...p })) }; };
const params = (f: Filtro) => ({ p_desde: f.desde || null, p_hasta: f.hasta || null, p_caja: f.caja || null });

function Tabla({ cols, rows, right = [] }: { cols: string[]; rows: (string | number)[][]; right?: number[] }) {
  return (
    <table className="w-full text-sm">
      <thead><tr className="border-b text-left text-xs text-muted-foreground">{cols.map((c, i) => <th key={i} className={`py-2 pr-3 ${right.includes(i) ? 'text-right' : ''}`}>{c}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="py-8 text-center text-muted-foreground">Sin datos</td></tr>}
        {rows.map((r, i) => <tr key={i} className="border-b last:border-0 hover:bg-muted/40">{r.map((c, j) => <td key={j} className={`py-2 pr-3 ${right.includes(j) ? 'text-right' : ''}`}>{c}</td>)}</tr>)}
      </tbody>
    </table>
  );
}

// --- Ventas por caja / cajero ---
export function RepVentas() {
  const { f, set } = useFiltro();
  const { data, isFetching } = useQuery<{ total: number; n: number; por_caja: { nombre: string; n: number; total: number }[]; por_cajero: { nombre: string; n: number; total: number }[] }>({
    queryKey: ['pos-rep-ventas', f], queryFn: () => posRpc('pos_rep_ventas', params(f)),
  });
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Ventas por caja / cajero</h1>
      <Filtros f={f} set={set} />
      <div className="text-sm text-muted-foreground">Total del período: <span className="font-semibold text-foreground">{money(data?.total ?? 0)}</span> en {data?.n ?? 0} venta(s) {isFetching && <Loader2 className="inline w-4 h-4 animate-spin" />}</div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Por caja</CardTitle></CardHeader><CardContent className="overflow-x-auto">
          <Tabla cols={['Caja', 'Ventas', 'Total']} right={[1, 2]} rows={(data?.por_caja ?? []).map((r) => [r.nombre, r.n, money(r.total)])} /></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Por cajero</CardTitle></CardHeader><CardContent className="overflow-x-auto">
          <Tabla cols={['Cajero', 'Ventas', 'Total']} right={[1, 2]} rows={(data?.por_cajero ?? []).map((r) => [r.nombre, r.n, money(r.total)])} /></CardContent></Card>
      </div>
    </div>
  );
}

// --- Cierres de caja (X/Z) ---
export function RepCierres() {
  const { f, set } = useFiltro();
  const { data = [] } = useQuery<{ codigo: string; caja: string; cajero: string | null; fecha_cierre: string; total_ventas: number; efectivo_sistema: number; efectivo_contado: number; diferencia: number }[]>({
    queryKey: ['pos-rep-cierres', f], queryFn: () => posRpc('pos_rep_cierres', params(f)),
  });
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Cierres de caja (X/Z)</h1>
      <Filtros f={f} set={set} />
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Cierres ({data.length})</CardTitle></CardHeader><CardContent className="overflow-x-auto">
        <Tabla cols={['Cierre', 'Caja', 'Cajero', 'Fecha', 'Ventas', 'Esperado', 'Contado', 'Diferencia']} right={[4, 5, 6, 7]}
          rows={data.map((r) => [r.codigo, r.caja, r.cajero ?? '—', new Date(r.fecha_cierre).toLocaleString(LOCALE), money(r.total_ventas), money(r.efectivo_sistema), money(r.efectivo_contado), money(r.diferencia)])} />
      </CardContent></Card>
    </div>
  );
}

// --- Ventas por forma de pago ---
export function RepFormasPago() {
  const { f, set } = useFiltro();
  const { data = [] } = useQuery<{ nombre: string; tipo: string; n: number; total: number }[]>({ queryKey: ['pos-rep-fp', f], queryFn: () => posRpc('pos_rep_formas_pago', params(f)) });
  const total = data.reduce((s, r) => s + Number(r.total), 0);
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><PieChart className="w-5 h-5" /> Ventas por forma de pago</h1>
      <Filtros f={f} set={set} />
      <Card><CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="text-base">Formas de pago</CardTitle><span className="text-sm">Total: <span className="font-semibold">{money(total)}</span></span></CardHeader><CardContent className="overflow-x-auto">
        <Tabla cols={['Forma de pago', 'Tipo', 'N°', 'Total']} right={[2, 3]} rows={data.map((r) => [r.nombre, r.tipo, r.n, money(r.total)])} />
      </CardContent></Card>
    </div>
  );
}

// --- Productos más vendidos ---
export function RepTopProductos() {
  const { f, set } = useFiltro();
  const { data = [] } = useQuery<{ codigo: string; nombre: string; cantidad: number; total: number }[]>({ queryKey: ['pos-rep-top', f], queryFn: () => posRpc('pos_rep_top_productos', { ...params(f), p_limite: 30 }) });
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Productos más vendidos</h1>
      <Filtros f={f} set={set} />
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Ranking ({data.length})</CardTitle></CardHeader><CardContent className="overflow-x-auto">
        <Tabla cols={['#', 'Código', 'Producto', 'Cantidad', 'Total']} right={[3, 4]} rows={data.map((r, i) => [i + 1, r.codigo, r.nombre, Number(r.cantidad).toLocaleString(LOCALE), money(r.total)])} />
      </CardContent></Card>
    </div>
  );
}

// --- Ventas por bodega (+ reclasificación ABC) ---
export function RepVentasBodega() {
  const dlg = useDialogoEnLinea();
  const { f, set } = useFiltro();
  const { data = [], refetch } = useQuery<{ nombre: string; clasificacion: string | null; n: number; total: number }[]>({ queryKey: ['pos-rep-bodega', f], queryFn: () => posRpc('pos_rep_ventas_bodega', { p_desde: f.desde || null, p_hasta: f.hasta || null }) });
  const reclasificar = useMutation({
    mutationFn: () => posRpc<{ clasificadas: number }>('pos_reclasificar_bodegas', {}),
    onSuccess: (r) => { toast.success(`${r.clasificadas} bodega(s) reclasificada(s) con ventas de 12 meses`); refetch(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Warehouse className="w-5 h-5" /> Ventas por bodega</h1>
        <Button variant="outline" isLoading={reclasificar.isPending} onClick={async () => { if (await dlg.confirmar('¿Reclasificar bodegas (A–F)?', { detalle: 'Usa las ventas de los últimos 12 meses.' })) reclasificar.mutate(); }}>
          <RefreshCw className="w-4 h-4 mr-1" /> Reclasificar ABC
        </Button>
      </div>
      <Filtros f={f} set={set} conCaja={false} />
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Ventas por bodega</CardTitle></CardHeader><CardContent className="overflow-x-auto">
        <Tabla cols={['Bodega', 'Clasif.', 'N°', 'Total']} right={[2, 3]} rows={data.map((r) => [r.nombre, r.clasificacion ?? '—', r.n, money(r.total)])} />
      </CardContent></Card>
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> La clasificación A–F usa las ventas de los últimos 12 meses; el botón recalcula bajo demanda (también lo hará el proceso nocturno).</p>
    </div>
  );
}
